import { authFetch } from "#app/utils/auth.client";
import {
  getWorkflowServiceContextHeaders,
  type WorkflowSvcContext,
} from "#app/utils/workflow-svc";

const svcUrl = () => ENV.WORKFLOW_SVC_PUBLIC_URL as string;

const POLICY_FETCH_TIMEOUT_MS = 15_000;

function withPolicyFetchTimeout<T>(
  request: Promise<T>,
  label: string,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(
      () => reject(new Error(`${label} timed out. Please retry.`)),
      POLICY_FETCH_TIMEOUT_MS,
    );
    request.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
}

export type PolicyListRule = {
  publicId: string;
  name: string | null;
  description: string | null;
  updatedAt: string;
  published?: boolean;
  /** `draft` until a version is published (server-derived). */
  state?: "draft" | "published";
  awaitingInput?: boolean;
  /** Default published version number when published. */
  publishedVersion?: number | null;
  stageCount?: number;
  ruleCount?: number;
  usedByCount?: number;
};

/** A draft: the server says so, or (an older server) no version is published. */
export function policyIsDraft(
  rule: Pick<PolicyListRule, "state" | "published">,
): boolean {
  return rule.state ? rule.state === "draft" : rule.published === false;
}

export type PolicyListCursor = { updatedAt: string; publicId: string };

export async function fetchPolicyList(args?: {
  search?: string;
  cursor?: PolicyListCursor | null;
  publishedOnly?: boolean;
  ctx?: WorkflowSvcContext;
}): Promise<{
  rules: PolicyListRule[];
  hasMore: boolean;
  nextCursor: PolicyListCursor | null;
}> {
  const qs = new URLSearchParams();
  if (args?.search?.trim()) qs.set("search", args.search.trim());
  if (args?.publishedOnly) qs.set("publishedOnly", "true");
  if (args?.cursor) {
    qs.set("cursorUpdatedAt", args.cursor.updatedAt);
    qs.set("cursorPublicId", args.cursor.publicId);
  }

  const res = await withPolicyFetchTimeout(
    authFetch(`${svcUrl()}/api/v1/rules${qs.toString() ? `?${qs}` : ""}`, {
      headers: getWorkflowServiceContextHeaders(args?.ctx),
    }),
    "Loading policies",
  );
  if (!res.ok) throw new Error("Failed to load policies");

  const data = (await res.json()) as {
    rules?: PolicyListRule[];
    hasMore?: boolean;
    nextCursor?: PolicyListCursor | null;
  };
  return {
    rules: (data.rules ?? []).map(mapPolicyListRule),
    hasMore: Boolean(data.hasMore),
    nextCursor: data.nextCursor ?? null,
  };
}

/** Published library grid — same page/limit contract as My Operators. */
export async function fetchPublishedPolicyLibrary(args: {
  page: number;
  pageSize: number;
  search?: string;
  ctx?: WorkflowSvcContext;
}): Promise<{
  rules: PolicyListRule[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}> {
  const qs = new URLSearchParams();
  qs.set("page", String(Math.max(1, args.page)));
  qs.set("limit", String(Math.min(100, Math.max(1, args.pageSize))));
  qs.set("publishedOnly", "true");
  if (args.search?.trim()) qs.set("search", args.search.trim());

  const res = await withPolicyFetchTimeout(
    authFetch(`${svcUrl()}/api/v1/rules?${qs}`, {
      headers: getWorkflowServiceContextHeaders(args.ctx),
    }),
    "Loading policies",
  );
  if (!res.ok) throw new Error("Failed to load policies");

  const data = (await res.json()) as {
    rules?: PolicyListRule[];
    total?: number;
    page?: number;
    limit?: number;
    totalPages?: number;
  };
  const pageSize = data.limit ?? args.pageSize;
  const total = data.total ?? 0;
  return {
    rules: (data.rules ?? []).map(mapPolicyListRule),
    total,
    page: data.page ?? args.page,
    pageSize,
    totalPages:
      data.totalPages ??
      Math.max(total === 0 ? 0 : 1, Math.ceil(total / pageSize)),
  };
}

function mapPolicyListRule(rule: PolicyListRule): PolicyListRule {
  return {
    publicId: rule.publicId,
    name: rule.name ?? null,
    description: rule.description ?? null,
    updatedAt: rule.updatedAt,
    published: rule.published,
    awaitingInput: rule.awaitingInput,
    publishedVersion: rule.publishedVersion ?? null,
    stageCount: rule.stageCount ?? 0,
    ruleCount: rule.ruleCount ?? 0,
    usedByCount: rule.usedByCount ?? 0,
  };
}

export async function fetchPolicyRule(
  ruleId: string,
): Promise<PolicyListRule | null> {
  const res = await authFetch(`${svcUrl()}/api/v1/rules/${ruleId}`, {
    headers: getWorkflowServiceContextHeaders(),
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error("Failed to load policy");
  const data = (await res.json()) as {
    publicId?: string;
    name?: string | null;
    description?: string | null;
    updatedAt?: string;
    published?: boolean;
    awaitingInput?: boolean;
  };
  if (!data.publicId) return null;
  return {
    publicId: data.publicId,
    name: data.name ?? null,
    description: data.description ?? null,
    updatedAt: data.updatedAt ?? new Date().toISOString(),
    published: data.published,
    awaitingInput: data.awaitingInput,
  };
}

export async function deletePolicyRule(ruleId: string): Promise<void> {
  const res = await authFetch(`${svcUrl()}/api/v1/rules/${ruleId}`, {
    method: "DELETE",
    headers: getWorkflowServiceContextHeaders(),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as {
      error?: string;
      workflows?: { workflowName: string; steps: string[] }[];
    };
    if (res.status === 409 && err.workflows?.length) {
      const list = err.workflows
        .map((w) => `${w.workflowName} (${w.steps.join(", ")})`)
        .join("; ");
      throw new Error(
        `${err.error ?? "This policy is still in use."} Used by: ${list}`,
      );
    }
    throw new Error(err.error ?? "Failed to delete policy");
  }
}
